import * as React from 'react';
import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import { TruncatedInputLabel } from 'styles/InputStyles';
import { IOptions } from 'utils/types';

import { FormControl, Tooltip } from '@mui/material';
import Autocomplete, {
  AutocompleteChangeDetails,
  AutocompleteProps,
} from '@mui/material/Autocomplete';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import { ItemOption } from './CustomAutocomplete.styles';

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  };

  const { key, ...optionProps } = dataSet[0];

  return (
    <Typography
      key={key}
      component="li"
      {...optionProps}
      noWrap
      style={inlineStyle}
    >
      {dataSet[1].label}
    </Typography>
  );
}

const OuterElementContext = createContext({});

const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
  const ref = useRef<VariableSizeList>(null);
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData: React.ReactElement<unknown>[] = [];
  (children as React.ReactElement<unknown>[]).forEach(
    (
      item: React.ReactElement<unknown> & {
        children?: React.ReactElement<unknown>[];
      },
    ) => {
      itemData.push(item);
      itemData.push(...(item.children || []));
    },
  );

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child: React.ReactElement<unknown>) => {
    if (child.hasOwnProperty('group')) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

export interface ICustomAutocompleteProps
  extends Omit<
    AutocompleteProps<IOptions, false, any, false, any>,
    'onChange' | 'renderInput'
  > {
  label: string;
  name: string;
  setValue: (option: IOptions | null) => void;
  onChange?: (
    event: React.SyntheticEvent,
    value: IOptions | null,
    reason: string,
    details?: AutocompleteChangeDetails<IOptions>,
  ) => void;
  required: boolean;
  error: boolean | undefined;
  helperText: string | undefined;
}

export default function CustomAutocomplete(props: ICustomAutocompleteProps) {
  const { setValue, options, helperText, ...autocompleteProps } = props;

  const handleChange = (
    event: React.SyntheticEvent,
    value: IOptions | null,
  ) => {
    if (value !== null) {
      setValue(value);
    }
  };

  const spanRef = useRef<HTMLLabelElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (spanRef.current) {
        setIsTruncated(
          spanRef.current.scrollWidth > spanRef.current.clientWidth,
        );
      }
    };
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [props.label]);

  return (
    <FormControl fullWidth variant="filled">
      <Autocomplete
        {...autocompleteProps}
        data-testid={`autocomplete-${props.name}`}
        clearText="Limpar"
        openText="Expandir"
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'clear') {
            setValue(null);
          }
        }}
        options={options}
        size="small"
        limitTags={1}
        id={props.id}
        noOptionsText="Nenhum dado encontrado."
        value={props.value || null}
        onChange={handleChange}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return option.label ?? '';
        }}
        renderInput={(params) => {
          return (
            <>
              <Tooltip
                title={isTruncated ? props.label : ''}
                arrow
                disableHoverListener={!isTruncated}
                placement="top"
              >
                <TruncatedInputLabel
                  htmlFor={params.id}
                  data-testid={`label-${props.name}`}
                >
                  <span ref={spanRef} className="label-text">
                    {props.label}
                  </span>
                  {props.required && !props.disabled && (
                    <span className="required-asterisk">*</span>
                  )}
                </TruncatedInputLabel>
              </Tooltip>

              <TextField
                {...params}
                helperText={helperText}
                required={props.required}
                error={props.error}
                variant="filled"
                hiddenLabel
                data-testid={`autocomplete-search-${props.name}`}
              />
            </>
          );
        }}
        renderOption={(props, option, state) =>
          [props, option, state.index] as React.ReactNode
        }
        slots={{
          popper: ItemOption,
        }}
        slotProps={{
          listbox: {
            component: ListboxComponent,
          },
        }}
      />
    </FormControl>
  );
}
